import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, HelperText, RadioGroup, SelectionCard, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { useScreenResolution } from '@app/hooks';
import { selectAddOns, setShowLastStepError, setVoiceFormValues } from '@app/redux/addOns';

import { DirectoryListType } from '@mfe/shared/schema-types';

import { TransitionWrapper } from '../TransitionWrapper';
import { Step } from './step';
import { StepInstanceProps } from '../../types';

import styles from '../../styles/configure-voice.module.scss';

export const Step4 = ({ isOpen, setOpenStep }: StepInstanceProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('ShopAddons');

  const {
    voiceConfig: { showLastStepError, directoryListingType, publishDirectoryListAddress },
  } = useSelector(selectAddOns);

  const handleOptionChange = (option: DirectoryListType) => {
    dispatch(setVoiceFormValues({ directoryListingType: option }));
    dispatch(setShowLastStepError(false));
  };

  const handleCheckboxChange = () => {
    dispatch(
      setVoiceFormValues({
        publishDirectoryListAddress: !publishDirectoryListAddress,
      })
    );
  };

  const showShareOption = [DirectoryListType.ListPublish, DirectoryListType.ListNotPublish].includes(
    directoryListingType as DirectoryListType
  );

  const { isLarge, isExtraLarge } = useScreenResolution();
  const isDesktop = isLarge || isExtraLarge;

  return (
    <Step
      step={4}
      isValid={!!directoryListingType}
      title={t('voice.directoryListing.title')}
      handleEdit={() => setOpenStep(4)}
    >
      <TransitionWrapper isOpen={isOpen}>
        {isOpen && (
          <div className={styles.stepFourContent}>
            <Txt variant="bodySmallRegular" color="subtle">
              {t('voice.directoryListing.description')}
            </Txt>
            <Txt variant="bodySmallBold" mt="24px" mb="16px">
              {t('voice.directoryListing.prompt')}
            </Txt>
            <RadioGroup
              layout={isDesktop ? 'horizontal' : 'vertical'}
              name="number-listing-selection"
              className={styles.selectionCards}
              mb={{ xs: '32px', md: '40px' }}
            >
              {[
                DirectoryListType.ListPublish,
                DirectoryListType.ListNotPublish,
                DirectoryListType.NotListNotPublish,
              ].map((type) => (
                <SelectionCard
                  selectionType="radio"
                  body={t(`voice.directoryListing.values.${type}`)}
                  id={type}
                  value={type}
                  checked={directoryListingType === type}
                  onChange={() => handleOptionChange(type)}
                />
              ))}
            </RadioGroup>
            {showLastStepError && (
              <HelperText
                state="error"
                helpMessage={t('voice.directoryListing.required')}
                className={styles.helperText}
              />
            )}
            {showShareOption && (
              <Checkbox
                // this selection needs to be submitted as publishDirectoryListAddress
                mb="40px"
                id="publish-directory-list-address"
                label={t('voice.directoryListing.share')}
                checked={publishDirectoryListAddress}
                onChange={handleCheckboxChange}
              />
            )}
          </div>
        )}
      </TransitionWrapper>
    </Step>
  );
};
